import "./InboundFlow.css";
import { useEffect, useRef, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useContextStateManagement } from "../../context/ContextApi";
import { useDispatch } from "react-redux";
import { logout } from "../../features/userSlice";
import { useNavigate } from "react-router-dom";
import CallLegsDialog from "../CallDialogs/CallLegsDialog";
import { usePracticeTimezone } from "../../hooks/usePracticeTimezone";
import { formatUsDateInPracticeTimezone } from "../../practiceTimezone";

export const InboundCallLogs = ({ formatDate, isDataGetWithDateInbound, setIsDataGetWithDateInbound, isCallsDataGet, setIsCallsDataGet, setIsDatesEmpty, isDatesEmpty, startDate, endDate, setInboundCallLogsData, inboundCallLogsData }) => {
    const [isTokenExpired, setIsTokenExpired] = useState(false);
    const [legsDialogOpen, setLegsDialogOpen] = useState(false);
    const [legsDialogGroup, setLegsDialogGroup] = useState(null);
    const flagRef = useRef(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { PracticeId, inboundContact, token, setActiveTab, setSideBarLinkActive, setToken, dateRangeRequest, setUserType } = useContextStateManagement();
    const practiceTimezone = usePracticeTimezone(PracticeId, token);

    const formatDateTime = (value) =>
        value ? formatUsDateInPracticeTimezone(value, practiceTimezone) : "-";

    const formatDuration = (value) => {
        const totalSeconds = Number(value || 0);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${String(seconds).padStart(2, "0")}`;
    };

    const formatLabel = (value) => {
        const formatted = String(value || "").toLowerCase();
        if (formatted === "no_answer") return "No Answer";
        return formatted
            .split("_")
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ");
    };

    const formatCost = (value, row) => {
        const amount = Number(value || 0);
        const currency = row?.PriceUnitNormalized || row?.PriceUnit || "USD";
        return `${amount.toFixed(4)} ${currency}`;
    };

    const columns = [
        {
            field: "DisplayStartedAt", headerName: "Started", width: 166, sortable: true, disableColumnMenu: true, align: "left", headerAlign: "left",
            renderCell: (params) => formatDateTime(params.value),
        },
        {
            field: "DisplayEndedAt", headerName: "Ended", width: 166, sortable: true, disableColumnMenu: true, align: "left", headerAlign: "left",
            renderCell: (params) => formatDateTime(params.value),
        },
        {
            field: "totalSeconds", headerName: "Total", width: 78, disableColumnMenu: true, align: "left", headerAlign: "left",
            renderCell: (params) => formatDuration(
                params.value ?? params.row?.DurationSec ?? params.row?.Duration ?? 0,
            ),
        },
        {
            field: "billableSeconds", headerName: "Billable", width: 92, disableColumnMenu: true, align: "left", headerAlign: "left",
            renderCell: (params) => {
                const secs = params.value ?? params.row?.DurationSec ?? params.row?.Duration ?? 0;
                const label = formatDuration(secs);
                return params.row?.hasTransfer ? `${label} *` : label;
            },
        },
        {
            field: "legCount", headerName: "Legs", width: 96, disableColumnMenu: true, align: "left", headerAlign: "left",
            renderCell: (params) => {
                const count = params.value || params.row?.legs?.length || 1;
                return (
                    <button
                        type="button"
                        className="simple-btn"
                        style={{ padding: "2px 8px", fontSize: 12 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setLegsDialogGroup({
                                rootCallSid: params.row?.RootCallSid || params.row?.Sid,
                                legs: params.row?.legs,
                                totalSeconds: params.row?.totalSeconds,
                                billableSeconds: params.row?.billableSeconds,
                                legCount: params.row?.legCount,
                                hasTransfer: params.row?.hasTransfer,
                            });
                            setLegsDialogOpen(true);
                        }}
                    >
                        View ({count})
                    </button>
                );
            },
        },
        {
            field: "PracticeDirection", headerName: "Practice Dir", width: 118, disableColumnMenu: true, align: "left", headerAlign: "left",
            renderCell: (params) => formatLabel(params.value),
        },
        { field: "Direction", headerName: "Twilio Dir", width: 122, disableColumnMenu: true, align: "left", headerAlign: "left" },
        {
            field: "LegLevel", headerName: "Root/Child", width: 96, disableColumnMenu: true, align: "left", headerAlign: "left",
            renderCell: (params) => String(params.value || "").toUpperCase(),
        },
        { field: "From", headerName: "From", width: 180, filterable: true, align: "left", headerAlign: "left" },
        { field: "To", headerName: "To", width: 190, filterable: true, align: "left", headerAlign: "left" },
        {
            field: "StatusCategory", headerName: "Status", width: 102, disableColumnMenu: true, align: "left", headerAlign: "left",
            renderCell: (params) => formatLabel(params.value || params.row?.Status),
        },
        {
            field: "BilledAmount", headerName: "Cost", width: 116, disableColumnMenu: true, align: "left", headerAlign: "left",
            renderCell: (params) => formatCost(params.value, params.row),
        },
        { field: "RootCallSid", headerName: "Root Call SID", width: 230, disableColumnMenu: true, align: "left", headerAlign: "left" },
        { field: "LegSid", headerName: "Leg SID", width: 230, disableColumnMenu: true, align: "left", headerAlign: "left" },     
    ];

    async function getCallsFetchHandle() {
        setIsDataGetWithDateInbound(false);
        setInboundCallLogsData([]);
        flagRef.current = true;

        let obj = { inboundContact };
        const userData = await dateRangeRequest(token, `/api/getInboundCalls`, obj);
        if (userData == "Token is expired, login again") {
            setIsTokenExpired(true)
        }
        else if (userData == "no record") {
            setInboundCallLogsData([]);
            setIsDataGetWithDateInbound(true);
        }
        else {
            setInboundCallLogsData(userData);
            setIsDataGetWithDateInbound(true);
            setIsCallsDataGet(true);
        }
    }

    useEffect(() => {
        if (flagRef.current === false || PracticeId) {
            // debugger;
            getCallsFetchHandle();
        }
    }, [flagRef, PracticeId])

    // Token-expiry redirect is now handled once, centrally, in ContextApi.js - see its comment
    // for why this component's own copy of the same effect was removed.

    return (
        <>
            <div className="grid-layout">
                <DataGrid
                    className="call-log-grid"
                    rows={inboundCallLogsData ? inboundCallLogsData : []}
                    rowHeight={56}
                    columns={columns}
                    autoWidth
                    pageSizeOptions={[10, 25, 50, 100]}
                    loading={isDataGetWithDateInbound === false}
                    disableColumnSelector
                    disableDensitySelector
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            printOptions: { disableToolbarButton: true },
                            csvOptions: { disableToolbarButton: true },
                        },
                    }}
                    getRowId={(row) => row.RootCallSid || row.LegSid || row.Sid || row.Id}
                    // disableColumnSorting
                    disableColumnMenu
                />
            </div>
            <CallLegsDialog
                open={legsDialogOpen}
                onClose={() => setLegsDialogOpen(false)}
                rootCallSid={legsDialogGroup?.rootCallSid}
                group={legsDialogGroup}
            />
        </>
    )
}


